import { Card, Colors, Spinner, Switch } from "@blueprintjs/core";
import { useMemo, useState } from "react";
import {
    CartesianGrid,
    Cell,
    ReferenceLine,
    ResponsiveContainer,
    Scatter,
    ScatterChart,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import css from "./CompareChart.module.scss";
import { isRelativeDeltaSignificant, MergedRow } from "./comparisons";

type MergedPoint = {
    key: string;
    a: number | null;
    b: number | null;
};

type PairedPoint = { key: string; a: number; b: number };

const SCATTER_COLOR = Colors.BLUE3;
const SCATTER_INCREASED_COLOR = Colors.RED3;
const SCATTER_DECREASED_COLOR = Colors.GREEN3;
const REFERENCE_COLOR = Colors.GRAY3;
export const CHART_HEIGHT = 480;

const filterPaired = (
    points: ReadonlyArray<MergedPoint>
): ReadonlyArray<PairedPoint> =>
    points.filter((p): p is PairedPoint => p.a != null && p.b != null);

// Returns the bottom 95% of points sorted by combined magnitude (a + b).
// Excludes the top 5% farthest from the origin — i.e. points whose values
// stretch the chart bounding box the most.
const bottomP95ByMagnitude = (
    points: ReadonlyArray<PairedPoint>
): ReadonlyArray<PairedPoint> => {
    if (points.length <= 1) {
        return points;
    }
    const sorted = [...points].sort((x, y) => x.a + x.b - (y.a + y.b));
    const cutoff = Math.max(1, Math.floor(sorted.length * 0.95));
    return sorted.slice(0, cutoff);
};

export const CompareChart = <TRow,>({
    merged,
    getValue,
    metricLabel,
    formatScalar,
    keyLabel,
    isLoading = false
}: {
    merged: ReadonlyArray<MergedRow<TRow>>;
    getValue: (row: TRow) => number | null;
    metricLabel: string;
    formatScalar: (v: number) => string;
    keyLabel: string;
    isLoading?: boolean;
}): JSX.Element => {
    const [hideOutliers, setHideOutliers] = useState(true);

    const points = useMemo<ReadonlyArray<MergedPoint>>(
        () =>
            merged.map((m) => ({
                key: m.key,
                a: m.a ? getValue(m.a) : null,
                b: m.b ? getValue(m.b) : null
            })),
        [merged, getValue]
    );

    if (isLoading) {
        return (
            <Card compact className={css["wrap"]}>
                <div
                    className={css["loading"]}
                    style={{ height: CHART_HEIGHT }}
                >
                    <Spinner />
                    <div>Loading results for all projects…</div>
                </div>
            </Card>
        );
    }

    return (
        <Card compact className={css["wrap"]}>
            <div className={css["header"]}>
                <Switch
                    checked={hideOutliers}
                    onChange={(e) => setHideOutliers(e.currentTarget.checked)}
                    label="Hide outliers (P95)"
                    className={css["outliers-switch"]}
                />
            </div>
            <ScatterView
                points={points}
                metricLabel={metricLabel}
                formatScalar={formatScalar}
                keyLabel={keyLabel}
                hideOutliers={hideOutliers}
            />
        </Card>
    );
};

type CategorizedPoint = PairedPoint & { fill: string };

const scatterFillFor = (p: PairedPoint): string => {
    if (!isRelativeDeltaSignificant(p.a, p.b)) {
        return SCATTER_COLOR;
    }
    return p.b > p.a ? SCATTER_INCREASED_COLOR : SCATTER_DECREASED_COLOR;
};

const ScatterView: React.FC<{
    points: ReadonlyArray<MergedPoint>;
    metricLabel: string;
    formatScalar: (v: number) => string;
    keyLabel: string;
    hideOutliers: boolean;
}> = ({ points, metricLabel, formatScalar, keyLabel, hideOutliers }) => {
    const data = useMemo<ReadonlyArray<CategorizedPoint>>(() => {
        const paired = filterPaired(points);
        const rendered = hideOutliers ? bottomP95ByMagnitude(paired) : paired;
        return rendered.map((p) => ({
            ...p,
            fill: scatterFillFor(p)
        }));
    }, [points, hideOutliers]);
    if (data.length === 0) {
        return <div className={css["empty"]}>No data to plot</div>;
    }
    const max = data.reduce((m, p) => Math.max(m, p.a, p.b), 0);
    return (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <ScatterChart
                data={[...data]}
                margin={{ top: 16, right: 24, left: 16, bottom: 48 }}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    type="number"
                    dataKey="a"
                    name="A"
                    domain={[0, max]}
                    tickFormatter={formatScalar}
                    label={{
                        value: `A: ${metricLabel}`,
                        position: "bottom",
                        offset: 8,
                        style: { fontWeight: 600, fontSize: 13 }
                    }}
                />
                <YAxis
                    type="number"
                    dataKey="b"
                    name="B"
                    domain={[0, max]}
                    width={80}
                    tickFormatter={formatScalar}
                    label={{
                        value: `B: ${metricLabel}`,
                        angle: -90,
                        position: "insideLeft",
                        offset: -8,
                        style: {
                            textAnchor: "middle",
                            fontWeight: 600,
                            fontSize: 13
                        }
                    }}
                />
                <ReferenceLine
                    segment={[
                        { x: 0, y: 0 },
                        { x: max, y: max }
                    ]}
                    stroke={REFERENCE_COLOR}
                    strokeDasharray="4 4"
                />
                <Tooltip
                    isAnimationActive={false}
                    cursor={{ strokeDasharray: "3 3" }}
                    content={({ active, payload }) => {
                        // TODO (phharvie): Do we want to handle when there are multiple points instead of just taking the first one?
                        if (!active || !payload || payload.length === 0) {
                            return null;
                        }
                        const point = payload[0]?.payload as
                            | CategorizedPoint
                            | undefined;
                        if (!point) {
                            return null;
                        }
                        return (
                            <div className={css["tooltip"]}>
                                <div className={css["tooltip-title"]}>
                                    <strong>
                                        {keyLabel} {point.key}
                                    </strong>
                                </div>
                                <div className={css["tooltip-row"]}>
                                    A: {formatScalar(point.a)}
                                </div>
                                <div className={css["tooltip-row"]}>
                                    B: {formatScalar(point.b)}
                                </div>
                            </div>
                        );
                    }}
                />
                <Scatter isAnimationActive={false}>
                    {data.map((p) => (
                        <Cell key={p.key} fill={p.fill} />
                    ))}
                </Scatter>
            </ScatterChart>
        </ResponsiveContainer>
    );
};
